import React from "react";
import { Layers, Database, Brain, Zap, ClipboardList } from "lucide-react";
import dashboardPanel from "../../assets/img/brochure/dashboard-panel.jpg";
import Reveal from "../common/Reveal";

const SOLUTIONS = [
  {
    name: "MT-ONE",
    subtitle: "통합 공장관리 플랫폼",
    icon: Layers,
    bar: "bg-primary-800",
    iconBg: "bg-primary-800",
    features: [
      "공장 관리 최적화: 생산, 품질, 물류, 공급망(SCM) 통합 관리",
      "로트(Lot) 추적: 생산 공정 및 품질 관리 강화",
      "설비 및 계측 데이터 연계: 장비 데이터를 통합하여 실시간 모니터링 가능",
      "웹 기반 최적화: 사용자 친화적인 웹 인터페이스 제공",
    ],
  },
  {
    name: "MT-DF",
    subtitle: "Data Factory",
    icon: Database,
    bar: "bg-accent-600",
    iconBg: "bg-accent-600",
    features: [
      "다양한 설비와 인터페이스 제공: OPC-UA, Modbus, RS485, RS232C 등 지원",
      "이기종 설비 연계: 여러 기종의 설비와 센서 데이터를 수집 및 분석 가능",
      "이상 감지 및 제조 빅데이터 분석: 설비 데이터 기반으로 이상 탐지 및 예측 분석 지원",
    ],
  },
  {
    name: "MT-NOVA",
    subtitle: "AI 분석 엔진",
    icon: Brain,
    bar: "bg-purple-600",
    iconBg: "bg-purple-600",
    features: [
      "AI 데이터 가공 및 분석 기능 제공",
      "AI 비전을 통한 품질 검사 / 공정 조건 최적화 및 설비 이상 감지",
      "OCR을 통한 문자 인식",
      "LLM을 통한 제조 현장 최적화 및 인사이트 제공",
    ],
  },
  {
    name: "MT-FEMS",
    subtitle: "Factory Energy Management System",
    icon: Zap,
    bar: "bg-green-600",
    iconBg: "bg-green-600",
    features: [
      "에너지 데이터 수집: 설비별 전력 및 에너지 사용량 실시간 수집",
      "사용현황 모니터링: 기간·공정·설비별 에너지 현황 통합 관리",
      "에너지 분석: 사용량·비용·원단위 분석을 통한 절감요소 도출",
    ],
  },
  {
    name: "MT-BAMS",
    subtitle: "Business Application Management System",
    icon: ClipboardList,
    bar: "bg-amber-600",
    iconBg: "bg-amber-600",
    features: [
      "사업별 맞춤 관리: 신청 항목, 제출서류, 평가기준 및 업무단계 설정",
      "업무정보 통합 관리: 기업, 협력사, 평가위원, 관련 문서를 하나의 시스템에서 관리",
      "진행현황·이력 관리: 처리상태, 평가결과, 알림, 변경이력을 실시간 관리",
    ],
  },
];

const SolutionLineup = () => {
  return (
    <section className="bg-gradient-to-b from-primary-50/60 to-white py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <span className="text-sm font-semibold uppercase tracking-wide text-accent-600">
            MTST Solution
          </span>
          <h2 className="mt-3 break-keep text-3xl font-bold text-primary-900 sm:text-4xl">
            데이터로 연결하고 AI로 혁신하는 스마트 제조
          </h2>
        </Reveal>

        {/* 히어로 미디어 */}
        <Reveal delay={100} className="mx-auto mt-10 flex max-w-4xl flex-col items-center gap-8 rounded-2xl bg-primary-950 p-6 shadow-xl sm:p-10 lg:flex-row">
          <div className="w-full max-w-md shrink-0">
            <img
              src={dashboardPanel}
              alt="MT-ALL MES 대시보드 화면"
              className="w-full rounded-lg shadow-lg"
            />
          </div>
          <p className="break-keep text-base leading-relaxed text-primary-100">
            설비·센서 데이터를 실시간으로 수집·통합하고, AI 분석 엔진으로
            이상감지·품질예측·공정 최적화를 지원하는 MTST의 통합 솔루션
            라인업입니다.
          </p>
        </Reveal>

        {/* 솔루션 카드 */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SOLUTIONS.map(({ name, subtitle, icon: Icon, bar, iconBg, features }, index) => (
            <Reveal
              key={name}
              delay={index * 80}
              className="relative h-full overflow-hidden rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className={`absolute inset-x-0 top-0 h-1 ${bar}`} />
              <div className="text-center">
                <div
                  className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${iconBg}`}
                >
                  <Icon className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="mt-3 text-xl font-bold text-primary-900">
                  {name}
                </h3>
                <p className="mt-1 break-keep text-sm text-gray-500">
                  {subtitle}
                </p>
              </div>
              <ul className="mt-5 space-y-2.5 border-t border-gray-100 pt-5">
                {features.map((f) => (
                  <li
                    key={f}
                    className="relative break-keep pl-3.5 text-sm leading-relaxed text-gray-600"
                  >
                    <span
                      className={`absolute left-0 top-2 h-1.5 w-1.5 rounded-full ${iconBg}`}
                    />
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionLineup;
